import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LoadingScreen from "./LoadingScreen";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { LOGO_SRC } from "../constants/branding";
import { normalizeAuthApiErrors } from "../utils/normalizeAuthApiErrors";
import GoogleSignInButton from "./GoogleSignInButton";
import useThrottleCooldown from "../hooks/useThrottleCooldown";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8002";

const Signup = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  // Public flag from the backend's existing registration_options endpoint.
  // `null` = still loading, so we don't flash the form before we know.
  // Default-true on fetch failure (don't lock the form behind a network blip).
  const [signupsEnabled, setSignupsEnabled] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/auth/registration-options/`)
      .then((r) => (r.ok ? r.json() : { signups_enabled: true }))
      .then((cfg) => { if (!cancelled) setSignupsEnabled(cfg.signups_enabled !== false); })
      .catch(() => { if (!cancelled) setSignupsEnabled(true); });
    return () => { cancelled = true; };
  }, []);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    passwordConfirm: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [wantsNewsletter, setWantsNewsletter] = useState(false);
  const [errors, setErrors] = useState({});
  const cooldown = useThrottleCooldown();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name] || errors.submit) {
      setErrors((prev) => {
        const next = { ...prev, [name]: "" };
        delete next.submit;
        return next;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name) {
      newErrors.name = "Name is required";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    // Password confirmation validation
    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = "Please confirm your password";
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = "Passwords do not match";
    }

    // Terms of Service + Privacy Policy must be accepted
    if (!acceptedTerms) {
      newErrors.acceptedTerms = "You must accept the Terms of Service and Privacy Policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted with data:", { ...formData, password: "***" });

    if (!validateForm()) {
      console.log("Form validation failed:", errors);
      return;
    }

    setIsLoading(true);

    try {
      console.log("Attempting to register user...");
      const response = await register(
        formData.email,
        formData.name,
        formData.password,
        formData.passwordConfirm,
        wantsNewsletter,
        acceptedTerms
      );
      console.log("Registration successful:", response);

      if (response?.verification_required === false) {
        toast.success(
          response.message ||
            "Account created. You can sign in now."
        );
        navigate("/login");
        return;
      }

      localStorage.setItem("pendingVerificationEmail", formData.email);
      // Flag the verify page to render the post-signup state (clearer
      // copy + prominent "Back to sign in" CTA).
      localStorage.setItem("justRegistered", "1");

      toast.success(
        `Account created! We sent a verification link to ${formData.email}. Check your inbox to continue.`,
        { duration: 12000 }
      );

      navigate("/verify");
    } catch (error) {
      console.error("Registration error:", error);

      // 429 — throttled. Start cooldown + show friendly message.
      if (error.code === "throttled" || error.response?.status === 429) {
        const seconds = Math.max(1, Number(error.retryAfter) || 60);
        cooldown.start(seconds);
        const friendly = `Too many signup attempts. Try again in ${
          seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds}s`
        }.`;
        setErrors({ submit: friendly });
        toast.error(friendly, { duration: 6000 });
        setIsLoading(false);
        return;
      }

      if (error.response?.data) {
        const normalized = normalizeAuthApiErrors(error.response.data);
        setErrors(normalized);

        const combined = `${normalized.email || ""} ${normalized.submit || ""}`.toLowerCase();
        if (combined.includes("already")) {
          localStorage.setItem("pendingVerificationEmail", formData.email);
          toast.error(
            normalized.email ||
              normalized.submit ||
              "An account with this email already exists.",
            {
              duration: 6000,
              action: {
                label: "Go to Login",
                onClick: () => navigate("/login"),
              },
            }
          );
        }
      } else {
        const errorMessage =
          error.message || "An error occurred. Please try again later.";
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }

      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <LoadingScreen title="Creating account" subtitle="Please wait…" />
    );
  }

  const inputBase =
    "font-secondary block w-full rounded-sm border px-4 py-3.5 text-[15px] text-white transition-colors placeholder:text-white/40 focus:outline-none focus:ring-1";
  const inputOk = `${inputBase} border-white/15 bg-[#0c0c0c] hover:border-white/25 focus:border-primary focus:ring-primary/30`;
  const inputErr = `${inputBase} border-red-500/80 bg-red-500/10 focus:border-red-500 focus:ring-red-500/30`;

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[min(50vh,400px)] w-[min(80vw,560px)] rounded-full bg-emerald-500/[0.05] blur-[90px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <motion.div
            className="mb-8 flex justify-start"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.08, duration: 0.45, ease: "easeOut" }}
          >
            <Link
              to="/"
              className="font-secondary inline-flex items-center rounded-sm px-2 py-2 text-[15px] text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <svg
                className="mr-2 h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Home
            </Link>
          </motion.div>

          <motion.div
            className="rounded-sm border border-white/10 bg-landing-section p-8 sm:p-10"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.5, ease: "easeOut" }}
          >
            <motion.div
              className="mb-8 text-center"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.45, ease: "easeOut" }}
            >
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-6 h-10 w-auto sm:h-11"
              />
              <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                Create your account
              </h2>
              <p className="font-secondary mt-2 text-[17px] leading-relaxed text-white/65">
                Join DataNodes and start using enterprise-grade RPC infrastructure.
              </p>
            </motion.div>

            {errors.submit && (
              <div
                className="font-secondary mb-6 rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                role="alert"
              >
                {errors.submit}
              </div>
            )}

            {signupsEnabled === false && (
              <div
                className="font-secondary mb-6 rounded-sm border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
                role="alert"
              >
                <p className="font-semibold">Signups are disabled on this environment.</p>
                <p className="mt-1 text-amber-200/80">
                  This instance is private. Already have an account?{" "}
                  <Link to="/login" className="underline hover:text-white">
                    Log in
                  </Link>
                  .
                </p>
              </div>
            )}

            <motion.div
              className="mb-6 space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.45 }}
            >
              {/* Same gate as the email/password Sign-up button: must accept
                  Terms + Privacy first. Newsletter opt-in flows through the
                  OAuth state param so first-time Google signups respect it.
                  Title hint nudges the user to the checkbox below if they
                  click while disabled. */}
              <GoogleSignInButton
                label="Sign up with Google"
                disabled={isLoading || signupsEnabled === false || !acceptedTerms}
                wantsNewsletter={wantsNewsletter}
                termsAccepted={acceptedTerms}
              />
              {!acceptedTerms && signupsEnabled !== false && (
                <p className="font-secondary -mt-2 text-center text-xs text-white/55">
                  Accept the Terms of Service and Privacy Policy below to continue with Google.
                </p>
              )}
              <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-white/40">
                <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
                <span className="font-secondary">or sign up with email</span>
                <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
              </div>
            </motion.div>

            <motion.form
              className="space-y-5"
              onSubmit={handleSubmit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.22, duration: 0.45 }}
            >
              <div>
                <label
                  htmlFor="name"
                  className="font-secondary mb-2 block text-sm font-medium text-white/80"
                >
                  Full name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  className={errors.name ? inputErr : inputOk}
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-400">{errors.name}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="font-secondary mb-2 block text-sm font-medium text-white/80"
                >
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  className={errors.email ? inputErr : inputOk}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="font-secondary mb-2 block text-sm font-medium text-white/80"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  className={errors.password ? inputErr : inputOk}
                  placeholder="At least 8 characters"
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-400">{errors.password}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="passwordConfirm"
                  className="font-secondary mb-2 block text-sm font-medium text-white/80"
                >
                  Confirm password
                </label>
                <input
                  type="password"
                  id="passwordConfirm"
                  name="passwordConfirm"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  className={errors.passwordConfirm ? inputErr : inputOk}
                  placeholder="Confirm your password"
                />
                {errors.passwordConfirm && (
                  <p className="mt-2 text-sm text-red-400">
                    {errors.passwordConfirm}
                  </p>
                )}
              </div>

              {/* Newsletter opt-in */}
              <div className="mt-2">
                <label className="flex items-start gap-3 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    checked={wantsNewsletter}
                    onChange={(e) => setWantsNewsletter(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-white/30 bg-[#0c0c0c] text-primary focus:ring-2 focus:ring-primary/50 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="font-secondary text-sm leading-relaxed text-white/75 group-hover:text-white/90 transition-colors">
                    Subscribe me to product updates and the DataNodes newsletter.
                  </span>
                </label>
              </div>

              {/* Terms of Service + Privacy Policy acceptance */}
              <div className="mt-2">
                <label className="flex items-start gap-3 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (errors.acceptedTerms) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.acceptedTerms;
                          return next;
                        });
                      }
                    }}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-white/30 bg-[#0c0c0c] text-primary focus:ring-2 focus:ring-primary/50 focus:ring-offset-0 cursor-pointer"
                    aria-describedby="terms-error"
                  />
                  <span className="font-secondary text-sm leading-relaxed text-white/75 group-hover:text-white/90 transition-colors">
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hover underline underline-offset-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Service
                    </Link>
                    {" "}and{" "}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hover underline underline-offset-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                {errors.acceptedTerms && (
                  <p id="terms-error" className="mt-2 text-sm text-red-400">
                    {errors.acceptedTerms}
                  </p>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={!acceptedTerms || signupsEnabled === false || cooldown.isCoolingDown}
                className="font-secondary mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
                whileHover={{ scale: acceptedTerms && signupsEnabled !== false ? 1.01 : 1 }}
                whileTap={{ scale: acceptedTerms && signupsEnabled !== false ? 0.99 : 1 }}
              >
                {signupsEnabled === false
                  ? "Signups disabled"
                  : cooldown.isCoolingDown
                    ? cooldown.label("Try again in")
                    : "Sign up"}
              </motion.button>
            </motion.form>

            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.28, duration: 0.45 }}
            >
              <p className="font-secondary text-[15px] text-white/65">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-primary transition-colors hover:text-primary-hover"
                >
                  Sign in
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
